import { Shield, Lock, Eye, Server, Bug, FileCheck } from "lucide-react";
import LegalPageLayout from "@/components/landing/LegalPageLayout";

const securityFeatures = [
  {
    icon: Lock,
    title: "256-bit AES Encryption",
    description: "All communications are encrypted with AES-256 both in transit (TLS 1.3) and at rest. Card numbers are tokenized and never stored in plain text.",
  },
  {
    icon: Eye,
    title: "Biometric Authentication",
    description: "Face ID, Touch ID, and fingerprint authentication protect access to your card and transaction approvals.",
  },
  {
    icon: Shield,
    title: "Non-Custodial Security",
    description: "Your funds remain in your own wallet at all times. We never hold, pool, or have access to your cryptocurrency assets.",
  },
  {
    icon: Server,
    title: "Decentralized Architecture",
    description: "Smart contracts handle transactions autonomously on the blockchain, eliminating single points of failure and central authority risks.",
  },
];

const certifications = [
  { title: "CertiK Audited", description: "Smart contracts and infrastructure regularly audited by CertiK, a leading blockchain security firm. All reports are published publicly." },
  { title: "SOC 2 Type II", description: "Independent verification of our security, availability, and confidentiality controls over a 12-month audit period." },
  { title: "ISO 27001", description: "International standard for information security management systems, certifying our comprehensive security framework." },
  { title: "Open Source", description: "Our smart contract code is open source and publicly verifiable, ensuring full transparency of our security model." },
];

const Security = () => (
  <LegalPageLayout title="Security" lastUpdated="January 15, 2026">
    <p className="text-lg">
      Security is the foundation of Trust Wallet Card. Our decentralized, non-custodial design ensures your assets remain under your full control at all times.
    </p>

    <section>
      <h2>Security Architecture</h2>
      <div className="!mt-6 grid gap-4 sm:grid-cols-2">
        {securityFeatures.map((feature) => {
          const Icon = feature.icon;
          return (
            <div key={feature.title} className="rounded-2xl border border-border/60 bg-card/80 p-6">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-primary">
                <Icon className="h-6 w-6" />
              </div>
              <h3 className="!mt-4">{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          );
        })}
      </div>
    </section>

    <section>
      <h2>Certifications & Audits</h2>
      <div className="!mt-6 grid gap-4 sm:grid-cols-2">
        {certifications.map((cert) => (
          <div key={cert.title} className="rounded-2xl border border-border/60 bg-card/80 p-6">
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-primary" />
              <h3 className="!mt-0 font-bold">{cert.title}</h3>
            </div>
            <p>{cert.description}</p>
          </div>
        ))}
      </div>
    </section>

    <section>
      <h2>Non-Custodial Design</h2>
      <p>
        Trust Wallet Card operates on a fully non-custodial model. Your cryptocurrency remains in your own wallet at all times. We only access the exact amount needed at the point of sale through a secure, time-limited smart contract authorization. We never hold, pool, or have custody over your assets.
      </p>
    </section>

    <section>
      <h2>Bug Bounty Program</h2>
      <div className="!mt-4 rounded-2xl border border-primary/20 bg-primary/5 p-6">
        <div className="flex items-center gap-2">
          <Bug className="h-5 w-5 text-primary" />
          <h3 className="!mt-0 font-bold text-primary">Report a Vulnerability</h3>
        </div>
        <p>
          We maintain an active bug bounty program with rewards up to <strong>$100,000</strong> for critical vulnerabilities. Responsible disclosure reports should be submitted through the Trust Wallet app. We commit to acknowledging reports within 24 hours.
        </p>
      </div>
    </section>
  </LegalPageLayout>
);

export default Security;
